import { Button } from "@/components/ui/baseUi/button"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName, successToastClassName } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { confirmEmailFieldsSchema, type ConfirmEmailFields } from "@/modules/auth/authTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { AuthContent, AuthForm, AuthHeader, AuthPage, AuthSubmitButton } from "@modules/auth/components/authComponents"
import { LoginBack } from "@modules/auth/components/loginBack"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { LoggerType } from "@modules/utils/loggerUtils"
import { autoSignIn, confirmSignUp, resendSignUpCode } from "aws-amplify/auth"
import { LoaderCircle } from "lucide-react"
import { useState } from "react"
import { Navigate, useLocation, useNavigate } from "react-router"
import { toast } from "sonner"

const defaultValues: ConfirmEmailFields = {
  code: "",
}

export function ConfirmEmailPage() {
  const [newCodeSendingStatus, setNewCodeSendingStatus] = useState<"idle" | "success" | "error" | "sending">("idle")

  const navigate = useNavigate()
  const location = useLocation() as { state?: { email?: string } }
  const email = location.state?.email
  const language = useLanguage()
  const resendConfirmationCode = async () => {
    try {
      if (!email) return
      setNewCodeSendingStatus("sending")

      await resendSignUpCode({ username: email })
      toast.success(word("auth.check_signup.form.notif_code_resend"), { classNames: successToastClassName() })
    } catch (e) {
      console.error(LoggerType.SignUp + "resend confirmation code", e)
      toast.error(word("global.error.default"), { classNames: errorToastClassName() })
    } finally {
      setNewCodeSendingStatus("idle")
    }
  }

  const confirmationCode = async () => {
    if (!email) return

    try {
      await confirmSignUp({ username: email, confirmationCode: form.state.values.code })
    } catch (e) {
      // Only confirmSignUp failing means the code is actually wrong or expired.
      console.error(LoggerType.SignUp + "confirmation code", e)
      toast.error(word("auth.check_signup.form.wrong_code"), { classNames: errorToastClassName() })
      return
    }

    // Code accepted, account confirmed. Try to resume the auto sign-in started by the sign-up flow.
    // When the user reached this page from the login flow (unconfirmed account), there is no auto
    // sign-in to resume and autoSignIn throws — that is NOT a wrong code, so just send them to login
    // with their email prefilled (the account is now confirmed and they can sign in normally).
    try {
      const result = await autoSignIn()
      if (result.nextStep.signInStep === "DONE") {
        await navigate(Path.Register)
        return
      }
    } catch (e) {
      console.error(LoggerType.SignUp + "auto sign in", e)
    }

    await navigate(Path.Login, { state: { email } })
  }

  const form = useAppForm({
    defaultValues,
    validators: {
      onChange: confirmEmailFieldsSchema(language),
      onSubmit: confirmEmailFieldsSchema(language),
      onBlur: confirmEmailFieldsSchema(language),
    },
    onSubmit: confirmationCode,
  })

  if (!email) {
    return <Navigate to={Path.Login} />
  }

  return (
    <AuthPage>
      <AuthForm
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
      >
        <AuthHeader
          title={word("auth.check_signup.form.check_account")}
          subtitle={word("auth.check_signup.form.check_account_subtitle")}
        />
        <AuthContent>
          <form.AppField name="code">
            {(field) => (
              <field.Field>
                <field.Content>
                  <field.Input name="code" placeholder={word("auth.check_signup.form.code")} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <Button
            variant="underline"
            onClick={() => void resendConfirmationCode()}
            size="xs"
            className="self-end"
            disabled={newCodeSendingStatus === "sending"}
          >
            {newCodeSendingStatus === "sending" && <LoaderCircle className="animate-spin" />}
            <Text fontStyle="BS">{word("auth.check_signup.form.resend_code")}</Text>
          </Button>

          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
            {([canSubmit, isSubmitting]) => (
              <AuthSubmitButton loading={isSubmitting} disabled={!canSubmit} buttonLabel={word("global.check")} />
            )}
          </form.Subscribe>

          <LoginBack />
        </AuthContent>
      </AuthForm>
    </AuthPage>
  )
}
